import { useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import type { AppDispatch, RootState } from "../app/store";
import { logout, getMe } from "../features/auth/authSlice";
import { getCookie, deleteCookie, TOKEN_COOKIE } from "../utils/cookies";
import { isTokenExpired } from "../features/tokenUtils";

const CHECK_INTERVAL_MS = 5 * 60 * 1000;

export const useAuthWatcher = () => {
  const dispatch = useDispatch<AppDispatch>();
  const isAuthenticated = useSelector((state: RootState) => !!state.auth.user);

  const checkIntervalRef = useRef<ReturnType<typeof setInterval>>();

  useEffect(() => {
    if (!isAuthenticated) return;

    const doLogout = () => {
      deleteCookie(TOKEN_COOKIE);
      dispatch(logout());
    };

    const checkTokenValidity = async () => {
      const token = getCookie(TOKEN_COOKIE);
      if (!token || isTokenExpired(token)) {
        doLogout();
        return;
      }

      try {
        await dispatch(getMe()).unwrap();
      } catch {
        doLogout();
      }
    };

    checkTokenValidity();

    checkIntervalRef.current = setInterval(
      checkTokenValidity,
      CHECK_INTERVAL_MS,
    );

    return () => {
      if (checkIntervalRef.current) clearInterval(checkIntervalRef.current);
    };
  }, [isAuthenticated, dispatch]);
};
